import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { renderPinLabel } from './pin-label.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un transistor TO-92. Forma en "D": lado plano
 * abajo (donde salen los pines), lado curvo arriba. Se dibuja como un
 * rectángulo con esquinas superiores muy redondeadas.
 */
export function renderTo92Body(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
  pinLabels: string[] = [],
): Konva.Group {
  const g = new Konva.Group();

  const width = def.body.length_mm ?? 5.08;
  const height = def.body.width_mm ?? 3.81;
  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.28);
  const veryLight = mix(bodyColor, '#ffffff', 0.55);
  const dark = mix(bodyColor, '#000000', 0.5);
  const topRadius = Math.min(def.body.corner_radius_mm ?? width / 2, width / 2);
  const outlineColor = '#000000';
  const outlineWidth = 0.1;

  // Geometría del cuerpo con TOP ACHATADO (elipse, no semicírculo)
  // y diagonales cortas en las esquinas superiores.
  const hL = width / 2;
  const hW = height / 2;
  const capH = topRadius * 0.55; // ry de la elipse (achatada)
  const diagH = Math.min(topRadius * 0.22, 0.9); // altura de la diagonal
  const cyArc = -hW + capH; // y donde la elipse se junta con los laterales
  const cyFront = cyArc + diagH; // y del top de la cara frontal / seam
  const ch = Math.min(width, height) * 0.12;
  const iL = hL - ch;

  // ctx aquí es Konva.Context (wraps CanvasRenderingContext2D pero
  // con typings distintos); usamos any para tratarlo directamente.
  const drawOuterPath = (ctx: any) => {
    ctx.beginPath();
    ctx.moveTo(-hL, hW);
    ctx.lineTo(hL, hW);
    ctx.lineTo(hL, cyArc);
    // Elipse achatada: rx = hL, ry = capH (< hL) → aparece como
    // un óvalo aplanado, no un semicírculo.
    ctx.ellipse(0, cyArc, hL, capH, 0, 0, Math.PI, true);
    ctx.lineTo(-hL, hW);
    ctx.closePath();
  };

  const bodyGroup = new Konva.Group({
    clipFunc: (ctx) => drawOuterPath(ctx),
  });

  // Paleta y gradiente global (arriba oscuro → abajo claro).
  const bevelOuter = mix(bodyColor, '#ffffff', 0.12);
  const bodyGradStops = [0, bevelOuter, 0.5, light, 1, veryLight];

  // Cuerpo.
  //   - De frente: degradado VERTICAL (que además hace visible el
  //     relieve del top cap por encima de los chaflanes / cara frontal).
  //   - Invertido: estamos viendo el TO-92 por su lomo. La cara plana
  //     queda al fondo y lo que vemos es la superficie CURVA, así que
  //     el degradado pasa a ser HORIZONTAL y cae hacia ambos lados
  //     (oscuro en los cantos, claro en la generatriz central), como
  //     un cilindro iluminado de frente.
  const bodyShape = new Konva.Shape({
    sceneFunc: (ctx, shape) => {
      drawOuterPath(ctx);
      ctx.fillStrokeShape(shape);
    },
    ...(flipped
      ? {
          fillLinearGradientStartPoint: { x: -hL, y: 0 },
          fillLinearGradientEndPoint: { x: hL, y: 0 },
          fillLinearGradientColorStops: [
            0, dark,
            0.22, light,
            0.46, veryLight,
            0.78, light,
            1, dark,
          ],
        }
      : {
          fillLinearGradientStartPoint: { x: 0, y: -hW },
          fillLinearGradientEndPoint: { x: 0, y: hW },
          fillLinearGradientColorStops: bodyGradStops,
        }),
    listening: true,
  });
  bodyShape.setAttr('nodeKind', 'body');
  bodyGroup.add(bodyShape);

  if (flipped) {
    // Brillo especular alargado siguiendo la generatriz del lomo,
    // desplazado a la izquierda del eje: refuerza la lectura de
    // superficie cilíndrica en lugar de cara plana.
    bodyGroup.add(new Konva.Ellipse({
      x: -hL * 0.30,
      y: cyFront + (hW - cyFront) * 0.15,
      radiusX: hL * 0.16,
      radiusY: (hW - cyArc) * 0.60,
      fill: '#ffffff',
      opacity: 0.18,
      listening: false,
    }));
    // Sombra de contacto en los dos cantos, para que la curvatura
    // no dependa sólo del degradado del relleno.
    for (const side of [-1, 1]) {
      bodyGroup.add(new Konva.Rect({
        x: side < 0 ? -hL : hL - hL * 0.14,
        y: cyArc,
        width: hL * 0.14,
        height: hW - cyArc,
        fillLinearGradientStartPoint: { x: side < 0 ? -hL : hL, y: 0 },
        fillLinearGradientEndPoint: { x: side < 0 ? -hL + hL * 0.14 : hL - hL * 0.14, y: 0 },
        fillLinearGradientColorStops: [0, mix(bodyColor, '#000000', 0.45), 1, 'rgba(0,0,0,0)'],
        listening: false,
      }));
    }
  }

  if (!flipped) {

    // Chaflán IZQUIERDO con TOP DIAGONAL: sube del vértice
    // exterior donde termina el lateral vertical (-hL, cyArc)
    // hasta la esquina interior del rectángulo frontal (-iL, cyFront).
    // Su borde inferior es horizontal a y=hW (sin chaflán inferior).
    bodyGroup.add(new Konva.Line({
      points: [-hL, cyArc, -iL, cyFront, -iL, hW, -hL, hW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    // Chaflán DERECHO: espejo.
    bodyGroup.add(new Konva.Line({
      points: [hL, cyArc, hL, hW, iL, hW, iL, cyFront],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));

    // Cara frontal: rectángulo con top a y=cyFront y bottom a y=hW
    // (extendido hasta el borde inferior, sin chaflán intermedio).
    bodyGroup.add(new Konva.Rect({
      x: -iL,
      y: cyFront,
      width: 2 * iL,
      height: hW - cyFront,
      cornerRadius: 0,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: [
        0, mix(bodyColor, '#000000', 0.55),
        0.5, mix(bodyColor, '#000000', 0.35),
        1, mix(bodyColor, '#000000', 0.1),
      ],
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      listening: false,
    }));
  }

  g.add(bodyGroup);

  // Contorno perimetral con la MISMA silueta (fuera del clip).
  g.add(new Konva.Shape({
    sceneFunc: (ctx, shape) => {
      drawOuterPath(ctx);
      ctx.fillStrokeShape(shape);
    },
    stroke: outlineColor,
    strokeWidth: outlineWidth,
    listening: false,
  }));

  // Etiqueta contra-transformada como el DIP: siempre a 0° world.
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    rotation: ps * (0 - rotationDeg),
    scaleY: ps,
    listening: false,
  });
  const fontSize = def.appearance.label?.font_size_mm ?? Math.min(width, height) * 0.28;
  const boxSize = Math.max(width, height) * 0.9;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, boxSize);
  g.add(labelGroup);

  // Etiquetas por pin (C/B/E o D/G/S) junto a la base de cada pin,
  // dentro del cuerpo.
  const pinFont = Math.min(width * 0.28, 1.2);
  for (let i = 0; i < def.pins.length; i++) {
    const label = pinLabels[i] ?? '';
    if (label) {
      g.add(renderPinLabel(def.pins[i]!, label, 1.4, rotationDeg, flipped, pinFont));
    }
  }

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
